import { CABIN_CLASS } from '@web-platform/shared-universal-constants/src/cabinClass';
import { SEARCH_TYPES } from '@web-platform/shared-universal-constants/src/searchTypes';
import {
  checkIsAnytime,
  DATE_TYPE,
  getDateType,
} from '@web-platform/shared-universal-utils-frontend-localisation';
import { getCommonsDateTime } from 'saddlebag-grappler';

import type {
  DateTimePrecision,
  Location,
  LocationAttributeLocationAttributeEncoding,
} from '@skyscanner-internal/schemas-types/commons.pb';
import type {
  CabinClass,
  PassengerGroup,
  PassengerGroupVersion,
} from '@skyscanner-internal/schemas-types/flights.pb';
import type { CabinClass as CabinClassConst } from '@web-platform/shared-universal-constants/src/cabinClass';
import type { DateTime } from '@web-platform/shared-universal-types/src/localisation';

/*
  Schema enums are string-valued at runtime (e.g. `BusinessDomain.COMBINED_SEARCH === "COMBINED_SEARCH"`),
  but TypeScript treats them nominally so a bare string literal cannot be assigned to the enum type.
  Importing the enum values pulls them into the bundle as objects. To avoid the bundle cost while
  keeping the enum types for type-safety on schema-typed fields, we narrow a string literal once
  via `asEnum`. Use the schema enum's string members verbatim so the runtime payload stays correct.
*/
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const asEnum = <T extends string>(value: string): T => value as T;

export const buildLocation = (
  id: string | undefined,
  locale: string,
  encoding: LocationAttributeLocationAttributeEncoding = asEnum<LocationAttributeLocationAttributeEncoding>(
    'SS_GEO2_ID',
  ),
): Location => ({
  locationAttribute: {
    locationId: id === SEARCH_TYPES.EVERYWHERE ? '1' : String(id),
    locale,
    locationAttributeEncoding: encoding,
  },
});

export const DATE_TIME_KIND_MAP: Record<number, DateTimePrecision> = {
  0: asEnum<DateTimePrecision>('MILLI'),
  1: asEnum<DateTimePrecision>('SECOND'),
  2: asEnum<DateTimePrecision>('DAY'),
  4: asEnum<DateTimePrecision>('WEEK'),
  5: asEnum<DateTimePrecision>('MONTH'),
  6: asEnum<DateTimePrecision>('YEAR'),
  7: asEnum<DateTimePrecision>('ANYTIME'),
  8: asEnum<DateTimePrecision>('MINUTE'),
};

export const isDateTimeKindValid = (dateTimeKind: number | undefined): dateTimeKind is number =>
  dateTimeKind !== undefined && dateTimeKind in DATE_TIME_KIND_MAP;

const buildDateTime = (
  date: DateTime | undefined,
  precision: DateTimePrecision = asEnum<DateTimePrecision>('MILLI'),
) => {
  if (typeof date === 'string' && checkIsAnytime(date)) {
    return {
      unix_time_millis: '1',
      date_time_kind: precision,
    };
  }
  if (date) {
    return getCommonsDateTime(date, true, precision);
  }
  return {
    unix_time_millis: String(new Date().getTime()),
    date_time_kind: precision,
  };
};

export const buildDate = (date: DateTime) => {
  const dateType = typeof date === 'string' ? getDateType(date) : DATE_TYPE.SPECIFIC_DATE;
  let actualDate: DateTime | undefined = date;
  let precision: DateTimePrecision = asEnum<DateTimePrecision>('DAY');
  if (dateType === DATE_TYPE.MONTH) {
    precision = asEnum<DateTimePrecision>('MONTH');
  } else if (dateType === DATE_TYPE.ANYTIME) {
    precision = asEnum<DateTimePrecision>('ANYTIME');
  } else if (dateType === DATE_TYPE.INVALID_DATE) {
    actualDate = undefined;
  }
  return buildDateTime(actualDate, precision);
};

export const buildCabinClass = (cabinClass: CabinClassConst): CabinClass => {
  switch (cabinClass) {
    case CABIN_CLASS.PREMIUM_ECONOMY:
      return asEnum<CabinClass>('PREMIUMECONOMY');
    case CABIN_CLASS.BUSINESS:
      return asEnum<CabinClass>('BUSINESS');
    case CABIN_CLASS.FIRST:
      return asEnum<CabinClass>('FIRST');
    case CABIN_CLASS.ECONOMY:
    default:
      return asEnum<CabinClass>('ECONOMY');
  }
};

export const buildPassengerGroup = ({
  adults,
  children,
  infants,
  nonAdultAges,
}: {
  adults: number;
  children: number;
  infants: number;
  nonAdultAges: number[];
}): PassengerGroup => ({
  groupModel: asEnum<PassengerGroupVersion>('ADULT_18_PLUS'),
  adultCount: adults,
  childCount: children,
  infantCount: infants,
  nonAdultAges,
});
